import React, { useState } from 'react'

import { formatCurrency, formatDate } from '../utils/utils'
import { ExpenseFormat } from '../interfaces/VehicleBase'
import ClientBase from '../interfaces/ClientBase'
import VehicleBase from '../interfaces/VehicleBase'
import { useAuth } from '../hooks/useAuth'
import Purchase from '../interfaces/Purchase'
import { DocStateSpecial } from '../enums/DocStates'
import DocToHeader from '../interfaces/DocInactive'
import { UserProfileRole } from '../modules/users/types/UserProfile'

import DocNotes from './DocNotes'
import { DocHeader, DocHeaderType } from './DocHeader'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import { ClientFrame, ClientFrameType } from './ClientFrame'
import { VehicleFrame, VehicleFrameType } from './VehicleFrame'
import DocFooter from './DocFooter'
import { ButtonAction, ButtonCheckConfirma, ButtonPrint } from './Buttons'
import { PurchaseFormat, PurchaseShort } from './PurchaseEdit'
import { DocStatusHeader } from './DocStatusHeader'
import ConfirmationModal from './ConfirmationModal'
import { Info } from './MessageInfo'
import { PrintableCard } from './PrintableCard'

const PurchasePreview: React.FC<{
  vehicle: VehicleBase
  client: ClientBase
  purchase: PurchaseShort | Purchase | PurchaseFormat
  costs: ExpenseFormat[]
  consultaSw: boolean
  creandoSw?: boolean
  modificandoSw?: boolean
  onGoBack?: () => void
  onCrearClick?: () => void
  onCancelModify?: () => void
  handleAnularClick?: (motivo: string) => void
  infoMessage?: string
  onInfoTimeout?: () => void
  isSubmitting?: boolean
}> = ({
  vehicle,
  client,
  purchase,
  costs,
  consultaSw,
  creandoSw = false,
  modificandoSw = false,
  onGoBack,
  onCrearClick,
  onCancelModify,
  handleAnularClick,
  infoMessage = '',
  onInfoTimeout = () => {},
  isSubmitting = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [motivo, setMotivo] = useState<string>('')
  const { userData, hasRole } = useAuth()

  const myNumberRes = (purchase?.number === 0 ? '' : purchase?.number) ?? ''
  const isAnulada: boolean = !!purchase?.voidedAt

  const docHeader: DocHeaderType = {
    urlImage: purchase.body.organization.pictures?.logotype,
    titulo: `Nota de Compra Nro. ${myNumberRes}`,
    isAnulada: isAnulada,
    tituloAnulada: 'NULA',
    fechaIniText: `Fecha`,
    fechaIniValue: formatDate(purchase?.date),
    fechaFinText: '',
    fechaFinValue: '',
  }

  let profile_name = ''

  if (purchase.body.profile) {
    profile_name = `${purchase.body.profile.user.firstname} ${purchase.body.profile.user.lastname}`
  } else {
    profile_name = userData?.user?.fullname ?? ''
  }

  const user = { name: profile_name, phone: purchase.body.profile?.user.phone ?? '' }
  const organizationFrame: OrganizationFrameType = {
    ...purchase.body.organization,
    user: user,
  }

  const client_name = `${client.firstname} ${client.lastname}`
  const clientFrame: ClientFrameType = {
    ...client,
    name: client_name,
  }

  const vehicleFrame: VehicleFrameType = {
    ...vehicle,
  }

  const profileName = profile_name
  const organizationName = purchase.body.organization.name

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'motivo') setMotivo(value)
  }

  const onCancelModClick = () => {
    setIsModalOpen(true)
  }

  const handleConfirm = () => {
    setIsModalOpen(false)
    onCancelModify && onCancelModify()
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (purchase?.voidedAt) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: purchase?.voidedAt ?? '',
      cause: purchase.voidedCause ?? '',
      by: purchase?.voidedBy_user ?? '',
    }
  }

  const puedeAnular = hasRole(UserProfileRole.VoidPurchases)

  const space = '\u00A0'
  const doc = 'Nota de compra'
  const buttonLabel = `${space}${space}Volver a la ${doc}${space}${space}`
  const buttonCreaLabel = creandoSw ? 'Crear' : 'Grabar cambios'

  return (
    <div className="p-2 max-w-3xl mx-auto">
      {infoMessage && (
        <div className="mb-4">
          <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} />
        </div>
      )}

      <PrintableCard>
        <DocStatusHeader docName="compra" state={docInactive} />
        <DocHeader header={docHeader} />
        <hr className="w-full border-t border-black-500 my-2" />

        <OrganizationFrame organization={organizationFrame} />

        <hr className="w-full border-t border-black-500 my-2" />
        <ClientFrame client={clientFrame} />

        <hr className="w-full border-t border-2 border-black-500 my-2" />
        <VehicleFrame vehicle={vehicleFrame} noPrice={true} />
        <hr className="w-full border-t border-2 border-black-500 my-2" />
        <DocNotes notes={purchase?.notes} />

        <div className="border-2 border-black pl-2 mt-2">
          <div className="grid grid-cols-2">
            <div className="pr-4">{'Precio de compra'}</div>
            <div className="pr-4 text-right">{formatCurrency(purchase.price)}</div>
          </div>
          <GridDet paycosts={costs} />
          <div className="grid grid-cols-2">
            <div className="pr-4 font-bold">{'Total a pagar'}</div>
            <div className="pr-4 text-right font-bold">{formatCurrency(purchase.total)}</div>
          </div>
        </div>
        <div className="border-2 border-black pl-2 mt-2">
          <div className="grid grid-cols-2">
            <div className="pr-4">{'Medio de pago'}</div>
            <div className="pr-4">{purchase.payment}</div>
          </div>
        </div>
        <div>
          Acepto la suma indicada <span className="font-bold">({purchase.totalInWords})</span>
          {` en ${purchase.payment}`} a mi entera satisfacción y sin ningún tipo de reparo posterior.
        </div>
        <DocFooter
          names={{
            profileName: profileName,
            organizationName: organizationName,
            clientName: client_name,
          }}
        />
      </PrintableCard>

      {consultaSw && <ButtonPrint />}
      {(creandoSw || modificandoSw) && onCrearClick && !isSubmitting && (
        <ButtonCheckConfirma
          title={'Confirma'}
          subTitle={''}
          checkLabel={`Confirmo que los datos de la ${doc} son correctos.`}
          buttonLabel={buttonCreaLabel}
          handleCrearClick={onCrearClick}
        />
      )}

      <div className="flex justify-center mt-1">
        {(creandoSw || modificandoSw) && onGoBack && (
          <div className="mr-4">
            <ButtonAction label={buttonLabel} handleButtonClick={onGoBack} />
          </div>
        )}
        {modificandoSw && onCancelModify && (
          <ButtonAction label={`Cancelar modificaciones`} handleButtonClick={onCancelModClick} />
        )}
      </div>

      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro? Perderás los cambios hechos."
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <div className="print:hidden">
        {!isAnulada && consultaSw && handleAnularClick && puedeAnular && (
          <div className="mt-2">
            <hr></hr>

            <ButtonCheckConfirma
              title={'Anular Compra'}
              subTitle={'Al anular la nota de compra, esta ya no será legalmente válida.'}
              checkLabel={`Estoy seguro de anular esta nota de compra.`}
              buttonLabel={'Anular'}
              handleCrearClick={() => handleAnularClick(motivo)}
              handleTextAreaChange={handleInputChange}
              textAreaLabel={`Motivo de anulación`}
              textAreaName="motivo"
              textAreaValue={motivo}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const GridDet: React.FC<{ paycosts: ExpenseFormat[] }> = ({ paycosts }) => {
  return (
    <div className="grid grid-cols-2">
      {paycosts.map((c) => (
        <>
          {!c.excluded && (
            <React.Fragment key={c._id}>
              <div className="pr-4">{c.description}</div>
              <div className="pr-4 text-right">-${c.cost_format}</div>
            </React.Fragment>
          )}
        </>
      ))}
    </div>
  )
}

export default PurchasePreview
